import React from 'react';

import { FmSlider } from '../FmSlider';
import { FmText } from '../FmComposition';
import { useFmDatedRasterCompare } from '../FmDatedRasterCompareContext';

function revealFromClientX(stage: HTMLElement, clientX: number): number {
  const rect = stage.getBoundingClientRect();
  if (rect.width <= 0) {
    throw new Error('Compare overlay stage has no width.');
  }
  return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
}

export function AnalysisWorkspaceCompareOverlay({
  earlierLabel,
  laterLabel,
}: {
  earlierLabel: string;
  laterLabel: string;
}) {
  const compare = useFmDatedRasterCompare();
  const stageRef = React.useRef<HTMLDivElement>(null);
  if (!compare) return null;
  const compareLabel = `${earlierLabel} → ${laterLabel}`;
  const seamStyle = { insetInlineStart: `${compare.reveal}%` };
  const setRevealFromPointer = (event: React.PointerEvent<HTMLButtonElement>) => {
    const stage = stageRef.current;
    if (!stage) {
      throw new Error('Compare overlay stage is not mounted.');
    }
    compare.setReveal(revealFromClientX(stage, event.clientX));
  };
  return (
    <div
      ref={stageRef}
      className="fm-analysis-workspace__swipe"
      data-testid="analysis-workspace-swipe"
    >
      <FmText
        as="span"
        size="caption"
        className="fm-analysis-workspace__swipe-date fm-analysis-workspace__swipe-date--earlier"
        style={seamStyle}
      >
        {earlierLabel}
      </FmText>
      <FmText
        as="span"
        size="caption"
        className="fm-analysis-workspace__swipe-date fm-analysis-workspace__swipe-date--later"
        style={seamStyle}
      >
        {laterLabel}
      </FmText>
      <div className="fm-analysis-workspace__swipe-seam" style={seamStyle}>
        <button
          type="button"
          className="fm-analysis-workspace__swipe-handle"
          aria-label={compareLabel}
          data-testid="analysis-workspace-swipe-handle"
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setRevealFromPointer(event);
          }}
          onPointerMove={(event) => {
            if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
            setRevealFromPointer(event);
          }}
        />
      </div>
      <div className="fm-analysis-workspace__swipe-input">
        <FmSlider
          min={0}
          max={100}
          step={1}
          value={compare.reveal}
          onChange={compare.setReveal}
          tone="inverse"
          aria-label={compareLabel}
          data-testid="dated-raster-compare"
        />
      </div>
    </div>
  );
}
